import { useCallback, useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import type { StoreApi } from 'zustand';

import type { JobProfileComponent } from '~/shared/data-contract/job-profile';

import type { DraftVersionStore } from '../job-profile-section/draft-version-store';
import type { ProfileForm } from '../job-profile-section/types';

type UseDraftFormSyncOptions = {
    readonly draftStore: StoreApi<DraftVersionStore>;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly hasDraft: boolean;
    readonly isEditing: boolean;
    readonly itemId: string;
};

export const useDraftFormSync = ({
    draftStore,
    formMethods,
    hasDraft,
    isEditing,
    itemId,
}: UseDraftFormSyncOptions): void => {
    const isUpdatingDraftRef = useRef(false);

    const handleLiveUpdate = useCallback(
        (data: { values: ProfileForm }) => {
            if (!isEditing || !hasDraft || isUpdatingDraftRef.current) {
                return;
            }

            isUpdatingDraftRef.current = true;

            try {
                const currentDraft = draftStore.getState().getDraft(itemId);

                if (currentDraft === undefined || currentDraft.jobProfile === undefined) {
                    isUpdatingDraftRef.current = false;

                    return;
                }

                const updatedProfile: JobProfileComponent = {
                    ...currentDraft.jobProfile,
                    competencies: new Map(data.values.competencies.map((competency) => [competency.id, competency])),
                    skills: data.values.skills,
                    translations: data.values.translations,
                };

                draftStore.getState().updateDraft(itemId, updatedProfile);
            } finally {
                isUpdatingDraftRef.current = false;
            }
        },
        [isEditing, itemId, draftStore, hasDraft],
    );

    const debouncedLiveUpdate = useDebounceCallback(handleLiveUpdate, 300);
    const debouncedLiveUpdateRef = useRef(debouncedLiveUpdate);
    debouncedLiveUpdateRef.current = debouncedLiveUpdate;

    useEffect(() => {
        if (!isEditing) {
            return;
        }

        const unsubscribe = formMethods.subscribe({
            callback: (data) => {
                debouncedLiveUpdateRef.current(data);
            },
            formState: {
                isDirty: true,
                values: true,
            },
        });

        return unsubscribe;
    }, [isEditing, formMethods]);
};
